import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { formatDate } from '@/lib/utils';
import { notificationService } from '@/services/notification.service';

/** Jeda penyegaran lencana. Cukup sering untuk terasa hidup, cukup jarang
 *  untuk tidak menembaki server dari setiap tab yang dibiarkan terbuka. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Lonceng notifikasi di topbar.
 *
 * Menampilkan kotak masuk PEMBACA SENDIRI, bukan seluruh lembaga: yang perlu
 * ditindaklanjuti seseorang adalah komentar pada pengumuman yang ia terbitkan.
 * Pengawasan menyeluruh ada di halaman Notifikasi, di balik izin tersendiri.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['notifications', 'bell'],
    queryFn: () => notificationService.list({ perPage: 8 }),
    refetchInterval: POLL_INTERVAL_MS,
    // Lencana yang basi lebih buruk daripada satu permintaan tambahan: guru
    // yang kembali ke tab ini ingin tahu keadaan sekarang, bukan satu menit lalu.
    refetchOnWindowFocus: true,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markAllMutation = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: refresh,
  });

  const openMutation = useMutation({
    mutationFn: (id: string) => notificationService.markRead(id),
    onSuccess: refresh,
  });

  const items = data?.items ?? [];
  const unread = data?.unreadCount ?? 0;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="relative rounded-[var(--radius-sm)] p-2 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-accent)] hover:text-[var(--color-foreground)]"
          aria-label={unread > 0 ? `Notifikasi, ${unread} belum dibaca` : 'Notifikasi'}
        >
          <Bell className="size-5" aria-hidden="true" />
          {unread > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-[var(--color-destructive)] px-1 text-[10px] font-bold leading-4 text-[var(--color-destructive-foreground)]"
              aria-hidden="true"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 w-80 max-w-[calc(100vw-2rem)] rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-popover)] p-1 shadow-[var(--shadow-popover)]"
        >
          <div className="flex items-center justify-between border-b border-[var(--color-border)] px-3 py-2.5">
            <p className="text-sm font-medium">Notifikasi</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
              >
                <CheckCheck className="size-3.5" aria-hidden="true" />
                Tandai dibaca
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-[var(--color-muted-foreground)]">
              Belum ada notifikasi.
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {items.map((item) => (
                <li key={item.id}>
                  <DropdownMenu.Item
                    onSelect={() => {
                      if (!item.isRead) openMutation.mutate(item.id);
                      if (item.linkPath) navigate(item.linkPath);
                    }}
                    className="cursor-pointer rounded-[var(--radius-sm)] px-3 py-2.5 outline-none data-[highlighted]:bg-[var(--color-accent)]"
                  >
                    <div className="flex gap-2">
                      {/* Titik penanda belum dibaca, bukan latar berwarna:
                          latar yang berubah warna bertabrakan dengan sorotan
                          keyboard pada item yang sama. */}
                      <span
                        className={
                          item.isRead
                            ? 'mt-1.5 size-1.5 shrink-0 rounded-full bg-transparent'
                            : 'mt-1.5 size-1.5 shrink-0 rounded-full bg-[var(--color-primary)]'
                        }
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        {item.subject && <p className="text-xs font-medium">{item.subject}</p>}
                        <p className="line-clamp-3 text-sm">{item.body}</p>
                        <p className="mt-0.5 text-xs text-[var(--color-muted-foreground)]">
                          {formatDate(item.createdAt, true)}
                        </p>
                      </div>
                    </div>
                  </DropdownMenu.Item>
                </li>
              ))}
            </ul>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-[var(--color-border)]" />

          <DropdownMenu.Item
            onSelect={() => navigate('/notifikasi')}
            className="cursor-pointer rounded-[var(--radius-sm)] px-3 py-2 text-center text-sm text-[var(--color-primary)] outline-none data-[highlighted]:bg-[var(--color-accent)]"
          >
            Lihat semua notifikasi
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
